import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as ErrorMonitor from 'core/ErrorMonitor';
import { useHistory } from 'react-router';
import { buildClientUrl } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';

import { useLanguageContext } from 'store/language';
import AuthLayout from 'components/AuthLayout';
import AppLoading from 'components/AppLoading';
import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';
import Button from 'components/Button';
import ActivateAccountForm from './components/ActivateAccountForm';

interface IUserProps {
  id: number;
  name: string;
  email: string;
  paidLeaveDays: number;
  paidLeaveDaysServedTime: number;
  paidLeaveDaysIrregularWorkingHours: number;
  paidLeaveDaysSpecificWorkingConditions: number;
  paidLeaveDaysAdditionalLeave: number;
  paidLeaveDaysByYear: [];
  wfhLimitType: string;
  remainingWfhLeavesPerWeek: number;
  remainingWfhLeavesPerMonth: number;
  featureLeavesIsActive: boolean;
  featureLeaveReview: boolean;
  featureLeavesHalfDayOption: boolean;
  featureForm76: boolean;
  featureColleaguesLeaves: boolean;
  featureExitInterview: boolean;
  featureSickLeaveWithoutAttachment: boolean;
  featureArt157LeaveWithoutAttachment: boolean;
  featureLeaveDaysServedTime: boolean;
  featureLeaveDaysIrregularWorkingHours: boolean;
  featureLeaveDaysSpecificWorkingConditions: boolean;
  featureLeaveDaysAdditionalLeave: boolean;
  featureHourlyAbsences: boolean;
  featureHealthAndSafetyTraining: boolean;
  featureWorkTrips: boolean;
  featureCustomFeatureForClientF: boolean;
  featureLimitWfhLeaveDays: boolean;
  featureShowWFHLeaveTypeInMainDropdown: boolean;
  featureLeaveSubstituteEmployee: boolean;
  featureRequireLeaveSubstituteEmployee: boolean;
  featureEmployeeCandidatesIsActive: boolean;
  featureGroupRoleLimitsIsActive: boolean;
  featureReminderForProbationPeriodEnd: boolean;
  featureLimitDocumentsToLeaves: boolean;
  reminderForIdCardExpiration: boolean;
  shouldDisallowLeaveDays: boolean;
  disallowedLeavesFrom: string;
  disallowedLeavesTo: string;
  isCandidate: boolean;
  employeeCandidateHelpText: string;
}

const ActivateScreen: FC = () => {
  const history = useHistory();
  const { client } = useActiveClientContext();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<IUserProps | null>(null);

  const { confirmationToken } = useParams<{
    confirmationToken: string | undefined;
  }>();
  const { setLanguage } = useLanguageContext();
  useEffect(() => {
    if (confirmationToken) {
      API.post('auth/account-activation-info', {
        confirmation_token: confirmationToken,
      })
        .then(res => {
          const {
            id,
            first_name: firstName,
            last_name: lastName,
            email,
            paid_leave_days: paidLeaveDays,
            paid_leave_served_time_days: paidLeaveDaysServedTime,
            paid_leave_irregular_working_hours_days: paidLeaveDaysIrregularWorkingHours,
            paid_leave_specific_working_conditions_days: paidLeaveDaysSpecificWorkingConditions,
            paid_leave_additional_days: paidLeaveDaysAdditionalLeave,
            paid_leave_days_by_year: paidLeaveDaysByYear,
            wfh_limit_type: wfhLimitType,
            remaining_wfh_leaves_per_week: remainingWfhLeavesPerWeek,
            remaining_wfh_leaves_per_month: remainingWfhLeavesPerMonth,
            feature_leaves_is_active: featureLeavesIsActive,
            feature_leave_review_is_active: featureLeaveReview,
            feature_leaves_half_day_option_is_active: featureLeavesHalfDayOption,
            feature_form_76_is_active: featureForm76,
            feature_colleagues_leaves_is_active: featureColleaguesLeaves,
            feature_exit_interview_is_active: featureExitInterview,
            feature_sick_leave_without_attachment_is_active: featureSickLeaveWithoutAttachment,
            feature_art157_leave_without_attachment_is_active: featureArt157LeaveWithoutAttachment,
            feature_leave_days_served_time_is_active: featureLeaveDaysServedTime,
            feature_leave_days_irregular_working_hours_is_active: featureLeaveDaysIrregularWorkingHours,
            feature_leave_days_specific_working_conditions_is_active: featureLeaveDaysSpecificWorkingConditions,
            feature_paid_additional_leave_is_active: featureLeaveDaysAdditionalLeave,
            feature_hourly_absences_is_active: featureHourlyAbsences,
            feature_health_and_safety_training_is_active: featureHealthAndSafetyTraining,
            feature_work_trips_is_active: featureWorkTrips,
            feature_custom_feature_for_client_f: featureCustomFeatureForClientF,
            feature_limit_wfh_leave_days: featureLimitWfhLeaveDays,
            feature_show_wfh_leave_type_in_main_dropdown: featureShowWFHLeaveTypeInMainDropdown,
            feature_leave_substitute_employee_is_active: featureLeaveSubstituteEmployee,
            feature_require_leave_substitute_employee_is_active: featureRequireLeaveSubstituteEmployee,
            feature_employee_candidates_is_active: featureEmployeeCandidatesIsActive,
            feature_group_role_limits_is_active: featureGroupRoleLimitsIsActive,
            feature_reminder_for_probation_period_end: featureReminderForProbationPeriodEnd,
            feature_limit_documents_to_leaves: featureLimitDocumentsToLeaves,
            reminder_for_id_card_expiration: reminderForIdCardExpiration,
            should_disallow_leave_days: shouldDisallowLeaveDays,
            disallowed_leaves_from: disallowedLeavesFrom,
            disallowed_leaves_to: disallowedLeavesTo,
            is_candidate: isCandidate,
            employee_candidate_help_text: employeeCandidateHelpText,
            lang,
          } = res.data?.user;
          ErrorMonitor.setUser({ id, email });
          // Update user info in storage
          setUserInfo({
            id,
            name: `${firstName} ${lastName}`,
            email,
            paidLeaveDays,
            paidLeaveDaysServedTime,
            paidLeaveDaysIrregularWorkingHours,
            paidLeaveDaysSpecificWorkingConditions,
            paidLeaveDaysAdditionalLeave,
            paidLeaveDaysByYear,
            wfhLimitType,
            remainingWfhLeavesPerWeek,
            remainingWfhLeavesPerMonth,
            featureLeavesIsActive,
            featureLeaveReview,
            featureLeavesHalfDayOption,
            featureForm76,
            featureColleaguesLeaves,
            featureExitInterview,
            featureSickLeaveWithoutAttachment,
            featureArt157LeaveWithoutAttachment,
            featureLeaveDaysServedTime,
            featureLeaveDaysIrregularWorkingHours,
            featureLeaveDaysSpecificWorkingConditions,
            featureLeaveDaysAdditionalLeave,
            featureHourlyAbsences,
            featureHealthAndSafetyTraining,
            featureWorkTrips,
            featureCustomFeatureForClientF,
            featureLimitWfhLeaveDays,
            featureShowWFHLeaveTypeInMainDropdown,
            featureLeaveSubstituteEmployee,
            featureRequireLeaveSubstituteEmployee,
            featureEmployeeCandidatesIsActive: featureEmployeeCandidatesIsActive ?? false,
            featureGroupRoleLimitsIsActive,
            featureReminderForProbationPeriodEnd,
            featureLimitDocumentsToLeaves,
            reminderForIdCardExpiration,
            shouldDisallowLeaveDays,
            disallowedLeavesFrom,
            disallowedLeavesTo,
            isCandidate,
            employeeCandidateHelpText,
          });
          setLanguage(lang);
        })
        .catch(err => setError(createErrorFromResponse(err).message))
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, []);

  if (isLoading) return <AppLoading />;

  return (
    <AuthLayout title={t('auth.accountActivation.title')}>
      {!!error && (
        <div className="d-flex flex-column justify-content-center">
          <p className="text-center text-error text-muted">{error}</p>
          <Button
            className="my-2"
            btnType="primary"
            onClick={() => history.push(buildClientUrl('/', client))}
          >
            {t('auth.accountActivation.gotoLogin')}
          </Button>
        </div>
      )}
      {!!userInfo && (
        <>
          <h4 className="text-center">{userInfo.name}</h4>
          <p className="text-center">{userInfo.email}</p>
          <ActivateAccountForm userInfo={userInfo} />
        </>
      )}
    </AuthLayout>
  );
};
export default ActivateScreen;
